import { FC } from 'react'
import { IconButton, Table, TableBody, TableRow } from '@mui/material'

import { SortableTableHeader, TableHeaderCell } from 'Components/Table/SortableTableHeader'
import { StyledPaper, StyledTableContainer } from 'Components/Table/StyledTableComponents'
import { StyledTableCell } from 'Components/Table/StyledTableCell'
import { CurrencyText } from 'Components/Text/CurrencyText'
import { Pagination } from 'Components/Table/Pagination'

import { useGetRecurringAllowancesQuery } from 'Hooks/api/JOONAdmin/useGetRecurringAllowancesQuery'
import { useDeleteUserRecurringAllowanceMutation } from 'Hooks/api/JOONAdmin/useDeleteUserRecurringAllowanceMutation'
import { usePagination } from 'Hooks/usePagination'
import { useTableOrder } from 'Hooks/useTableOrder'
import { useConfirmationModalContext } from 'Hooks/modals/useConfirmationModal'

import {
  convertRecurringAllowanceToTableRow,
  RecurringAllowanceRow
} from 'Utils/helpers/recurringAllowance'
import { sortTableRows } from 'Utils/helpers/tables'
import { RecurringAllowance } from 'Utils/types/allowance'
import { red } from 'Utils/styles/colors'
import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons'

const headerCells: readonly TableHeaderCell<RecurringAllowanceRow>[] = [
  { id: 'id', numeric: true, label: 'Id' },
  { id: 'allowanceType', numeric: false, label: 'Type' },
  { id: 'amount', numeric: true, label: 'Amount' },
  { id: 'cadence', numeric: false, label: 'Cadence' },
  { id: 'duration', numeric: false, label: 'Duration' },
  { id: 'startDate', numeric: false, label: 'Start Date' },
  { id: 'nextOccurrence', numeric: false, label: 'Next Occurrence' },
  { id: 'active', numeric: false, label: 'Active' },
  { id: 'notes', numeric: false, label: 'Notes' }
] as const

type UserRecurringAllowancesTableProps = {
  benefitProgramId: number
  userId: number
  onEditRecurringAllowance: (recurringAllowance: RecurringAllowance) => void
}

export const UserRecurringAllowancesTable: FC<UserRecurringAllowancesTableProps> = ({
  benefitProgramId,
  userId,
  onEditRecurringAllowance
}) => {
  const { order, orderBy, handleRequestSort } = useTableOrder<RecurringAllowanceRow>()
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination()
  const { showModal } = useConfirmationModalContext()
  const deleteRecurringAllowanceMutation = useDeleteUserRecurringAllowanceMutation()

  const { data } = useGetRecurringAllowancesQuery({
    userId,
    benefitProgramId,
    pageNumber: page + 1,
    perPage: rowsPerPage
  })

  const handleDelete = async (recurringAllowance: RecurringAllowance) => {
    const result = await showModal(
      `Delete Recurring Allowance`,
      `Are you sure you want to delete recurring allowance ID ${recurringAllowance.id}? This action cannot be undone.`
    )

    if (result) {
      deleteRecurringAllowanceMutation.mutate({ id: recurringAllowance.id })
    }
  }

  if (!data) return null

  const { recurringAllowances, totalRecurringAllowances } = data

  const rows = recurringAllowances.map((recurringAllowance) =>
    convertRecurringAllowanceToTableRow(recurringAllowance)
  )

  return (
    <>
      <StyledPaper style={{ height: 'fit-content' }}>
        <StyledTableContainer>
          <Table>
            <SortableTableHeader
              headerCells={headerCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              hideActionColumn={false}
              actionColumnName="Actions"
            />
            <TableBody>
              {sortTableRows(rows, order, orderBy).map((row) => (
                <TableRow key={row.id}>
                  <StyledTableCell>{row.id}</StyledTableCell>
                  <StyledTableCell>{row.allowanceType}</StyledTableCell>
                  <StyledTableCell>
                    <CurrencyText value={row.amount} currencyCode={row.currencyCode} />
                  </StyledTableCell>
                  <StyledTableCell>{row.cadence}</StyledTableCell>
                  <StyledTableCell>{row.duration}</StyledTableCell>
                  <StyledTableCell>{row.startDate}</StyledTableCell>
                  <StyledTableCell>{row.nextOccurrence}</StyledTableCell>
                  <StyledTableCell>{row.active ? 'Yes' : 'No'}</StyledTableCell>
                  <StyledTableCell title={row.notes || ''}>{row.notes || '-'}</StyledTableCell>
                  <StyledTableCell>
                    <IconButton
                      size="small"
                      onClick={() => onEditRecurringAllowance(row.recurringAllowance)}
                      aria-label="Edit recurring allowance"
                      style={{ marginRight: '8px' }}
                    >
                      <Pencil1Icon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(row.recurringAllowance)}
                      aria-label="Delete recurring allowance"
                      style={{ color: red }}
                    >
                      <TrashIcon />
                    </IconButton>
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </StyledPaper>
      <Pagination
        optionText="recurring allowances"
        totalRows={totalRecurringAllowances}
        rowsPerPage={rowsPerPage}
        page={page}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  )
}
